import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Link,
  IconButton,
  Fab,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  KeyboardArrowUp,
  KeyboardArrowDown,
  Email,
  Facebook,
  Instagram,
  LinkedIn,
  Security,
  Speed,
  TrendingUp,
  OpenInNew,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { authen } from '../../firebase/firebase';
import Logoapp from '../../../assets/img/logoapp.png';

interface ModernFooterProps {
  hideFab?: boolean;
}

const ModernFooter: React.FC<ModernFooterProps> = ({ hideFab = false }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredSocial, setHoveredSocial] = useState<number | null>(null);
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [user, setUser] = useState<any>();
  const [isAtTop, setIsAtTop] = useState(true);
  const [showFab, setShowFab] = useState(!hideFab);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(authen, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  // Scroll position detection for smart FAB behavior
  useEffect(() => {
    const handleScroll = () => {
      // Check for custom scroll container (like in Landing page)
      const customScrollContainer = document.querySelector('[data-scroll-container]');
      
      let scrollTop: number;
      let containerHeight: number;
      let documentHeight: number;
      
      if (customScrollContainer) {
        // Use custom scroll container
        scrollTop = customScrollContainer.scrollTop;
        containerHeight = customScrollContainer.clientHeight;
        documentHeight = customScrollContainer.scrollHeight;
      } else {
        // Use window scroll
        scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        containerHeight = window.innerHeight;
        documentHeight = document.documentElement.scrollHeight;
      }
      
      // Determine if we're at the top or near the footer
      const isNearFooter = scrollTop + containerHeight >= documentHeight - 200;
      setIsAtTop(!isNearFooter);
    };

    // Listen to both window and custom container scroll events
    const customScrollContainer = document.querySelector('[data-scroll-container]');
    
    if (customScrollContainer) {
      customScrollContainer.addEventListener('scroll', handleScroll);
    } else {
      window.addEventListener('scroll', handleScroll);
    }
    
    handleScroll(); // Initial check
    
    return () => {
      if (customScrollContainer) {
        customScrollContainer.removeEventListener('scroll', handleScroll);
      } else {
        window.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);

  useEffect(() => {
    const handleMouseMove = (e: Event) => {
      const mouse = e as MouseEvent;
      const rect = document.getElementById('futuristic-footer')?.getBoundingClientRect();
      if (rect) {
        setMousePos({
          x: ((mouse.clientX - rect.left) / rect.width) * 100,
          y: ((mouse.clientY - rect.top) / rect.height) * 100,
        });
      }
    };

    const footer = document.getElementById('futuristic-footer');
    footer?.addEventListener('mousemove', handleMouseMove);
    return () => footer?.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const scrollToTop = () => {
    const customScrollContainer = document.querySelector('[data-scroll-container]');
    if (customScrollContainer) {
      customScrollContainer.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };
  
  const scrollToFooter = () => {
    const footer = document.getElementById('futuristic-footer');
    if (footer) {
      const customScrollContainer = document.querySelector('[data-scroll-container]');
      if (customScrollContainer) {
        customScrollContainer.scrollTo({ 
          top: customScrollContainer.scrollHeight, 
          behavior: 'smooth' 
        });
      } else {
        footer.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const handleFabClick = () => {
    if (isAtTop) {
      scrollToFooter();
    } else {
      scrollToTop();
    }
  };

  const handleHomeNavigation = () => {
    if (!user || user.isAnonymous) {
      navigate('/');
    } else {
      navigate('/home');
    }
  };

  const footerSections = [
    {
      title: "Company Info",
      icon: Security,
      color: "#3b82f6",
      gradient: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
      links: [
        { text: "About Us", href: "/about-us" },
        { text: "Privacy Policy", href: "/privacy-policy" },
        { text: "Terms & Conditions", href: "/terms-of-service" },
        { text: "Contact Us", href: "/contact-us" }
      ]
    },
    {
      title: "Features",
      icon: Speed,
      color: "#8b5cf6",
      gradient: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
      links: [
        { text: "Home", href: "/home", isHome: true },
        { text: "My Profile", href: "/profile" },
        { text: "My Progress", href: "/myProgress" },
        { text: "Indian Schools", href: "/indian-schools" }
      ]
    },
    {
      title: "Resources",
      icon: TrendingUp,
      color: "#f59e0b",
      gradient: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
      links: [
        { text: "Username Sign Up Instructions", href: "/resources#school-onboarding" },
        { text: "SSO Sign Up Instructions", href: "/resources#sso-onboarding" },
        // { text: "Newsletter", href: "/newsletter" },
      ]
    },
  ];  

  const socialLinks = [
    { name: "Facebook", href: "https://www.facebook.com/spellingheroapp/", icon: Facebook, color: "#1877f2" },
    { name: "Instagram", href: "https://www.instagram.com/spellingheroapp/", icon: Instagram, color: "#e4405f" },
    { name: "LinkedIn", href: "https://www.linkedin.com/company/spellinghero/", icon: LinkedIn, color: "#0077b5" }
  ];

  return (
    <Box
      id="futuristic-footer"
      component="footer"
      sx={{
        position: 'relative',
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
        borderTop: '1px solid rgba(34, 197, 94, 0.1)',
        overflow: 'hidden',
        minHeight: '400px',
        // Enhanced keyframes with all original animations
        '@keyframes float': {
          '0%, 100%': { transform: 'translateY(0px) rotate(0deg)', opacity: 0.6 },
          '50%': { transform: 'translateY(-15px) rotate(2deg)', opacity: 0.8 }
        },
        '@keyframes pulse': {
          '0%, 100%': { transform: 'scale(1)', opacity: 0.7 },
          '50%': { transform: 'scale(1.05)', opacity: 1 }
        },
        '@keyframes slideInUp': {
          '0%': { transform: 'translateY(60px)', opacity: 0 },
          '100%': { transform: 'translateY(0)', opacity: 1 }
        },
        '@keyframes fadeInScale': {
          '0%': { transform: 'scale(0.8)', opacity: 0 },
          '100%': { transform: 'scale(1)', opacity: 1 }
        },
        '@keyframes shimmer': {
          '0%': { backgroundPosition: '-200% 0' },
          '100%': { backgroundPosition: '200% 0' }
        },
        '@keyframes energyPulse': {
          '0%, 100%': { transform: 'scale(1)', opacity: 0.6 },
          '50%': { transform: 'scale(1.05)', opacity: 1 }
        },
        '@keyframes particleFloat': {
          '0%': { transform: 'translateY(100vh) translateX(0) rotate(0deg)', opacity: 0 },
          '10%': { opacity: 1 },
          '90%': { opacity: 1 },
          '100%': { transform: 'translateY(-100px) translateX(100px) rotate(360deg)', opacity: 0 }
        },
        '@keyframes holographicShift': {
          '0%, 100%': { backgroundPosition: '0% 50%' },
          '50%': { backgroundPosition: '100% 50%' }
        },
        // Advanced decorative elements
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: `
            linear-gradient(90deg, 
              transparent 0%, 
              rgba(34, 197, 94, 0.3) 10%, 
              rgba(16, 185, 129, 0.6) 25%, 
              rgba(34, 197, 94, 0.8) 50%, 
              rgba(16, 185, 129, 0.6) 75%, 
              rgba(34, 197, 94, 0.3) 90%, 
              transparent 100%
            )
          `,
          backgroundSize: '200% 100%',
          animation: 'shimmer 4s ease-in-out infinite',
          borderRadius: '0 0 2px 2px',
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: -80,
          left: -80,
          width: 200,
          height: 200,
          background: `radial-gradient(
            circle,
            rgba(16, 185, 129, 0.08) 0%,
            rgba(16, 185, 129, 0.04) 50%,
            transparent 70%
          )`,
          borderRadius: '50%',
          animation: 'pulse 6s ease-in-out infinite',
          animationDelay: '2s',
        }
      }}
    >
      {/* Floating particles */}
      {[...Array(8)].map((_, i) => (
        <Box
          key={i}
          sx={{
            position: 'absolute',
            width: Math.random() * 6 + 4,
            height: Math.random() * 6 + 4,
            background: `
              radial-gradient(circle, 
                rgba(34, 197, 94, ${Math.random() * 0.5 + 0.3}) 0%, 
                rgba(34, 197, 94, ${Math.random() * 0.3 + 0.1}) 70%, 
                transparent 100%
              )
            `,
            borderRadius: '50%',
            left: `${Math.random() * 100}%`,
            animation: `particleFloat ${Math.random() * 10 + 15}s linear infinite`,
            animationDelay: `${Math.random() * 5}s`,
            filter: 'blur(1px)',
            zIndex: 1,
          }}
        />
      ))}

      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2, py: { xs: 4, md: 6 } }}>
        {/* Content Grid with Logo moved to first card */}
        <Grid container spacing={3} sx={{ mb: 5 }}>
          
          {/* Enhanced Logo Card */}
          <Grid item xs={12} md={6} lg={3}>
            <Card
              sx={{
                height: 220,
                background: `
                  linear-gradient(145deg,
                    rgba(255, 255, 255, 0.98) 0%,
                    rgba(240, 253, 244, 0.95) 50%,
                    rgba(236, 253, 245, 0.9) 100%
                  )
                `,
                backdropFilter: 'blur(25px)',
                borderRadius: 6,
                border: '2px solid rgba(34, 197, 94, 0.15)',
                boxShadow: `
                  0 20px 40px -10px rgba(0, 0, 0, 0.1),
                  inset 0 1px 0 rgba(255, 255, 255, 0.6)
                `,
                transform: isVisible ? 'translateY(0) rotateX(0deg)' : 'translateY(60px) rotateX(15deg)',
                opacity: isVisible ? 1 : 0,
                transition: 'all 0.8s cubic-bezier(0.23, 1, 0.32, 1)',
                transitionDelay: '0.2s',
                position: 'relative',
                overflow: 'hidden',
                '&:hover': {
                  transform: 'translateY(-15px) rotateY(-3deg)',
                  boxShadow: `
                    0 30px 60px -15px rgba(34, 197, 94, 0.3),
                    0 0 0 1px rgba(34, 197, 94, 0.4),
                    inset 0 1px 0 rgba(255, 255, 255, 0.8)
                  `,
                  borderColor: 'rgba(34, 197, 94, 0.6)',
                },
                // Holographic effect
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: `
                    linear-gradient(45deg,
                      transparent 30%,
                      rgba(34, 197, 94, 0.05) 50%,
                      transparent 70%
                    )
                  `,
                  opacity: 0,
                  transition: 'opacity 0.4s ease',
                  pointerEvents: 'none',
                  animation: 'holographicShift 3s ease-in-out infinite',
                }
              }}
            >
              <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                {/* Enhanced Logo */}
                <Box
                  sx={{
                    position: 'relative',
                    width: { xs: 135, md: 180 },
                    height: { xs: 60, md: 80 },
                    mb: 2,
                    '&:hover img': {
                      transform: 'scale(1.1) rotate(5deg)',
                      filter: 'drop-shadow(0 15px 30px rgba(34, 197, 94, 0.4))',
                    }
                  }}
                >
                  {/* Holographic rings */}
                  {[...Array(3)].map((_, i) => (
                    <Box
                      key={i}
                      sx={{
                        position: 'absolute',
                        inset: -8 - i * 4,
                        border: `1px solid rgba(34, 197, 94, ${0.3 - i * 0.1})`,
                        borderRadius: '50%',
                        animation: `energyPulse ${2 + i * 0.5}s ease-in-out infinite`,
                        animationDelay: `${i * 0.2}s`,
                      }}
                    />
                  ))}
                  <img
                    src={Logoapp}
                    alt="SpellingHero Logo"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      filter: 'drop-shadow(0 8px 16px rgba(34, 197, 94, 0.2))',
                      animation: isVisible ? 'pulse 3s ease-in-out infinite' : 'none',
                    }}
                  />
                </Box>
                {/* Enhanced Tagline */}
                <Typography 
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    textAlign: 'center',
                    background: 'linear-gradient(135deg, #16a34a 0%, #15803d 60%, #166534 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    color: 'transparent',
                    letterSpacing: '0.3px',
                    lineHeight: 1.3,
                  }}
                >
                  Upgrade your spelling skills today!
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Enhanced Section Cards */}
          {footerSections.map((section, index) => (
            <Grid item xs={12} md={6} lg={3} key={section.title}>
              <Card
                onMouseEnter={() => setHoveredCard(index)}
                onMouseLeave={() => setHoveredCard(null)}
                sx={{
                  height: 220,
                  background: hoveredCard === index
                    ? `
                      linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)
                    `
                    : `
                      linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)
                    `,
                  backdropFilter: 'blur(25px)',
                  borderRadius: 6,
                  border: hoveredCard === index
                    ? '2px solid #4ade80'
                    : '1px solid #86efac',
                  boxShadow: hoveredCard === index
                    ? `
                      0 20px 40px rgba(42, 146, 53, 0.15), 
                      0 8px 16px rgba(42, 146, 53, 0.1),
                      inset 0 1px 0 rgba(255, 255, 255, 0.8)
                    `
                    : `
                      0 2px 8px rgba(42, 146, 53, 0.08),
                      inset 0 1px 0 rgba(255, 255, 255, 0.6)
                    `,
                  transform: isVisible ? 'translateY(0) rotateX(0deg)' : 'translateY(60px) rotateX(15deg)',
                  opacity: isVisible ? 1 : 0,
                  transition: 'all 0.8s cubic-bezier(0.23, 1, 0.32, 1)',
                  transitionDelay: `${(index + 1) * 0.2}s`,
                  position: 'relative',
                  overflow: 'hidden',
                  '&:hover': {
                    transform: 'translateY(-15px) rotateY(-3deg)',
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 4,
                    background: 'linear-gradient(135deg, #2A9235 0%, #225532 100%)',
                    opacity: hoveredCard === index ? 1 : 0,
                    transition: 'opacity 0.4s ease',
                    borderRadius: '6px 6px 0 0',
                  },
                  // Holographic effect
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: `
                      linear-gradient(45deg,
                        transparent 30%,
                        rgba(34, 197, 94, 0.05) 50%,
                        transparent 70%
                      )
                    `,
                    opacity: hoveredCard === index ? 1 : 0,
                    transition: 'opacity 0.4s ease',
                    pointerEvents: 'none',
                  }
                }}
              >
                <CardContent sx={{ p: 3, height: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        background: hoveredCard === index
                          ? '#2A9235'
                          : '#ffffff',
                        borderRadius: 3,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        transform: hoveredCard === index ? 'scale(1.1) rotate(5deg)' : 'scale(1)',
                        border: '1px solid #dcfce7',
                        boxShadow: '0 2px 4px rgba(42, 146, 53, 0.1)',
                      }}
                    >
                      <section.icon 
                        sx={{ 
                          color: hoveredCard === index ? 'white' : '#2A9235',
                          fontSize: 24,
                          transition: 'all 0.3s ease'
                        }} 
                      />
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 'bold',
                        color: hoveredCard === index ? '#225532' : '#2A9235',
                        transition: 'all 0.3s ease',
                      }}
                    >
                      {section.title}
                    </Typography>
                  </Box>
                  <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, flex: 1 }}>
                    {section.links.map((link, linkIndex) => (
                      <Box
                        component="li"
                        key={link.text}
                        sx={{ 
                          mb: 1.5,
                          transform: isVisible ? 'translateX(0)' : 'translateX(-20px)',
                          opacity: isVisible ? 1 : 0,
                          transition: 'all 0.4s ease',
                          transitionDelay: `${(index + 1) * 0.15 + linkIndex * 0.1}s`,
                        }}
                      >
                        <Link
                          href={link.isHome ? undefined : link.href}
                          onClick={link.isHome ? (e) => { e.preventDefault(); handleHomeNavigation(); } : undefined}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            color: '#6b7280',
                            textDecoration: 'none',
                            fontWeight: 500,
                            fontSize: '0.9rem',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            py: 0.5,
                            px: 1,
                            borderRadius: 2,
                            position: 'relative',
                            cursor: 'pointer',
                            '&::before': {
                              content: '""',
                              position: 'absolute',
                              left: 0,
                              top: '50%',
                              transform: 'translateY(-50%)',
                              width: 0,
                              height: 2,
                              backgroundColor: '#2A9235',
                              borderRadius: 1,
                              transition: 'width 0.3s ease',
                            },
                            '&:hover': {
                              color: '#2A9235',
                              backgroundColor: 'rgba(42, 146, 53, 0.1)',
                              transform: 'translateX(8px)',
                              '&::before': {
                                width: 16,
                              }
                            },
                            '& .external-icon': {
                              opacity: 0,
                              transition: 'all 0.3s ease',
                              fontSize: '14px',
                              marginLeft: '6px',
                            },
                            '&:hover .external-icon': {
                              opacity: 1,
                              transform: 'translateX(4px) scale(1.2)'
                            }
                          }}
                        >
                          {link.text}
                          <OpenInNew className="external-icon" />
                        </Link>
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Enhanced Bottom Section */}
        <Card
          sx={{
            background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
            backdropFilter: 'blur(20px)',
            borderRadius: 4,
            border: '1px solid rgba(34, 197, 94, 0.2)',
            boxShadow: '0 16px 48px rgba(34, 197, 94, 0.1)',
            transform: isVisible ? 'translateY(0)' : 'translateY(40px)',
            opacity: isVisible ? 1 : 0,
            transition: 'all 1s cubic-bezier(0.4, 0, 0.2, 1)',
            transitionDelay: '0.8s',
            overflow: 'hidden',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '2px',
              background: 'linear-gradient(90deg, transparent 0%, #22c55e 20%, #16a34a 50%, #22c55e 80%, transparent 100%)',
              animation: 'shimmer 3s ease-in-out infinite',
              backgroundSize: '200% 100%',
            }
          }}
        >
          <CardContent sx={{ p: 4 }}>
            {/* Three column layout */}
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
              gap: 4,
              alignItems: 'center',
              textAlign: { xs: 'center', md: 'left' }
            }}>
              
              {/* Enhanced Get in Touch */}
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                justifyContent: { xs: 'center', md: 'flex-start' },
                transform: isVisible ? 'translateX(0)' : 'translateX(-40px)',
                opacity: isVisible ? 1 : 0,
                transition: 'all 0.6s ease',
                transitionDelay: '1s',
              }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Email sx={{ color: 'white', fontSize: 20 }} />
                </Box>
                <Box>

                  <Link
                    href="mailto:support@pragnatyx.com"
                    sx={{
                      color: '#22c55e',
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      textDecoration: 'none',
                      '&:hover': { 
                        color: '#16a34a',
                        textDecoration: 'underline',
                      }
                    }}
                  >
                    support@pragnatyx.com
                  </Link>
                </Box>
              </Box>

              {/* Enhanced Copyright */}
              <Box sx={{
                textAlign: 'center',
                transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
                opacity: isVisible ? 1 : 0,
                transition: 'all 0.6s ease',
                transitionDelay: '1.1s',
              }}>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 700,
                    color: '#1f2937',
                  }}
                >
                  © 2025 SpellingHero
                </Typography>
              </Box>

              {/* Enhanced Social Links */}
              <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: 'center',
                justifyContent: { xs: 'center', md: 'flex-end' },
                gap: 2
              }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 800,
                    color: '#1f2937',
                    transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
                    opacity: isVisible ? 1 : 0,
                    transition: 'all 0.6s ease',
                    transitionDelay: '1.2s',
                  }}
                >
                  Join Our Community
                </Typography>
                <Box sx={{
                  display: 'flex',
                  gap: 2,
                  transform: isVisible ? 'translateX(0)' : 'translateX(40px)',
                  opacity: isVisible ? 1 : 0,
                  transition: 'all 0.6s ease',
                  transitionDelay: '1.4s',
                }}>
                  {socialLinks.map((social, index) => (
                    <IconButton
                      key={social.name}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      onMouseEnter={() => setHoveredSocial(index)}
                      onMouseLeave={() => setHoveredSocial(null)}
                      sx={{
                        width: 56,
                        height: 56,
                        background: hoveredSocial === index
                          ? `
                            linear-gradient(135deg,
                              ${social.color} 0%,
                              ${social.color}dd 50%,
                              ${social.color}bb 100%
                            )
                          `
                          : `
                            linear-gradient(145deg,
                              rgba(255, 255, 255, 0.9) 0%,
                              rgba(248, 250, 252, 0.8) 100%
                            )
                          `,
                        boxShadow: hoveredSocial === index
                          ? `
                            0 20px 40px ${social.color}50,
                            0 0 0 3px ${social.color}30,
                            inset 0 2px 0 rgba(255, 255, 255, 0.3)
                          `
                          : `
                            0 15px 30px rgba(0,0,0,0.15),
                            inset 0 1px 0 rgba(255, 255, 255, 0.8)
                          `,
                        borderRadius: 4,
                        border: `2px solid ${hoveredSocial === index ? 'transparent' : social.color}30`,
                        transition: 'all 0.6s cubic-bezier(0.23, 1, 0.32, 1)',
                        position: 'relative',
                        overflow: 'hidden',
                        '&:hover': {
                          transform: 'translateY(-15px) scale(1.15) rotate(-5deg)',
                        },
                        // Holographic rings
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          inset: -4,
                          border: `1px solid ${social.color}40`,
                          borderRadius: 4,
                          opacity: hoveredSocial === index ? 1 : 0,
                          transition: 'opacity 0.4s ease',
                          animation: hoveredSocial === index ? 'energyPulse 2s ease-in-out infinite' : 'none',
                        },
                        '&::after': {
                          content: `"${social.name}"`,
                          position: 'absolute',
                          bottom: -50,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          backgroundColor: '#1f2937',
                          color: 'white',
                          padding: '6px 12px',
                          borderRadius: '6px',
                          fontSize: '14px',
                          fontWeight: 700,
                          opacity: hoveredSocial === index ? 1 : 0,
                          transition: 'all 0.4s cubic-bezier(0.23, 1, 0.32, 1)',
                          whiteSpace: 'nowrap',
                          zIndex: 10,
                          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
                        }
                      }}
                    >
                      <social.icon
                        sx={{
                          color: hoveredSocial === index ? 'white' : social.color,
                          fontSize: 28,
                          transition: 'all 0.3s ease',
                          transform: hoveredSocial === index ? 'scale(1.2)' : 'scale(1)',
                        }}
                      />
                    </IconButton>
                  ))}
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Container>

      {/* Smart Floating Action Button */}
      {showFab && (
        <Fab
          onClick={handleFabClick}
          sx={{
            position: 'fixed',
            bottom: 32,
            right: 32,
            width: 64,
            height: 64,
            background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 50%, #15803d 100%)',
            color: 'white',
            boxShadow: '0 16px 48px rgba(34, 197, 94, 0.4)',
            transform: isVisible ? 'translateY(0) scale(1)' : 'translateY(100px) scale(0.8)',
            opacity: isVisible ? 1 : 0,
            transition: 'all 1.2s cubic-bezier(0.4, 0, 0.2, 1)',
            transitionDelay: '1.6s',
            border: '3px solid rgba(255, 255, 255, 0.3)',
            backdropFilter: 'blur(10px)',
            zIndex: 1300,
            animation: isVisible ? 'fabPulse 2s ease-in-out infinite, fabFloat 3s ease-in-out infinite' : 'none',
            '&:hover': {
              transform: 'translateY(-12px) scale(1.15) rotate(-5deg)',
              boxShadow: '0 24px 64px rgba(34, 197, 94, 0.6)',
              background: 'linear-gradient(135deg, #16a34a 0%, #15803d 50%, #166534 100%)',
              animation: 'fabBounce 0.6s ease-in-out, fabPulse 1.5s ease-in-out infinite',
            },
            '&:active': {
              transform: 'translateY(-8px) scale(1.05)',
              animation: 'fabClick 0.3s ease-in-out',
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: -3,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #22c55e, #16a34a, #15803d, #22c55e)',
              backgroundSize: '400% 400%',
              animation: 'shimmer 4s ease-in-out infinite',
              opacity: 0.7,
              zIndex: -1,
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              inset: -8,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #22c55e, #16a34a, #15803d, #22c55e)',
              opacity: 0.3,
              zIndex: -2,
              animation: 'fabRipple 2s ease-in-out infinite',
            }
          }}
        >
          {isAtTop ? (
            <KeyboardArrowDown
              sx={{
                fontSize: 36,
                animation: 'float 2s ease-in-out infinite',
                transition: 'all 0.3s ease',
              }}
            />
          ) : (
            <KeyboardArrowUp
              sx={{
                fontSize: 36,
                animation: 'float 2s ease-in-out infinite',
                transition: 'all 0.3s ease',
              }}
            />
          )}
        </Fab>
      )}

      {/* Add CSS animations */}
      <style>{`
        @keyframes shimmer {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        
        @keyframes float {
          0% { transform: translateY(0); }
          50% { transform: translateY(-6px); }
          100% { transform: translateY(0); }
        }

        @keyframes fabPulse {
          0% { 
            box-shadow: 0 16px 48px rgba(34, 197, 94, 0.4);
            transform: scale(1);
          }
          50% { 
            box-shadow: 0 20px 60px rgba(34, 197, 94, 0.6);
            transform: scale(1.02);
          }
          100% { 
            box-shadow: 0 16px 48px rgba(34, 197, 94, 0.4);
            transform: scale(1);
          }
        }

        @keyframes fabFloat {
          0% { transform: translateY(0px) rotate(0deg); }
          25% { transform: translateY(-3px) rotate(1deg); }
          50% { transform: translateY(-6px) rotate(0deg); }
          75% { transform: translateY(-3px) rotate(-1deg); }
          100% { transform: translateY(0px) rotate(0deg); }
        }

        @keyframes fabBounce {
          0% { transform: translateY(0) scale(1); }
          25% { transform: translateY(-8px) scale(1.1); }
          50% { transform: translateY(-12px) scale(1.15); }
          75% { transform: translateY(-6px) scale(1.05); }
          100% { transform: translateY(0) scale(1); }
        }

        @keyframes fabClick {
          0% { transform: scale(1); }
          50% { transform: scale(0.95); }
          100% { transform: scale(1); }
        }

        @keyframes fabRipple {
          0% { 
            transform: scale(1);
            opacity: 0.3;
          }
          50% { 
            transform: scale(1.2);
            opacity: 0.1;
          }
          100% { 
            transform: scale(1.4);
            opacity: 0;
          }
        }

        @keyframes fabGlow {
          0% { 
            filter: brightness(1) drop-shadow(0 0 5px rgba(34, 197, 94, 0.3));
          }
          50% { 
            filter: brightness(1.1) drop-shadow(0 0 15px rgba(34, 197, 94, 0.6));
          }
          100% { 
            filter: brightness(1) drop-shadow(0 0 5px rgba(34, 197, 94, 0.3));
          }
        }
      `}</style>
    </Box>
  );
};

export default ModernFooter;
